import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GeoIntent, GeoUseCase } from "@/lib/types";
import { GEO_INTENTS, getGroupedUseCases } from "@/data/geo-constants";
import { cn } from "@/lib/utils";

export type GeoFilters = {
  intent?: GeoIntent;
  useCase?: GeoUseCase;
};

interface GeoFiltersBarProps {
  filters: GeoFilters;
  onFiltersChange: (filters: GeoFilters) => void;
}

export function GeoFiltersBar({ filters, onFiltersChange }: GeoFiltersBarProps) {
  const groupedUseCases = getGroupedUseCases();
  const hasActiveFilters = filters.intent || filters.useCase;

  const handleIntentClick = (intent: GeoIntent) => {
    onFiltersChange({
      ...filters,
      intent: filters.intent === intent ? undefined : intent,
    });
  };

  const handleUseCaseChange = (useCase: string) => {
    onFiltersChange({
      ...filters,
      useCase: useCase === "all" ? undefined : (useCase as GeoUseCase),
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="bg-black/20 backdrop-blur-sm rounded-xl border border-white/5 p-3 md:p-4">
      <div className="flex flex-col gap-3">
        {/* Intent Pills */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-white/40 mr-1">Intent:</span>
          {GEO_INTENTS.map((intentOption) => (
            <button
              key={intentOption.value}
              onClick={() => handleIntentClick(intentOption.value)}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200",
                filters.intent === intentOption.value
                  ? "bg-gradient-to-r from-brand-purple to-brand-cyan text-white shadow-[0_0_12px_rgba(0,194,255,0.3)]"
                  : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border border-white/10"
              )}
            >
              {intentOption.label}
            </button>
          ))}
        </div>

        {/* Use Case Dropdown + Clear */}
        <div className="flex items-center gap-3">
          <Select
            value={filters.useCase || "all"}
            onValueChange={handleUseCaseChange}
          >
            <SelectTrigger className="w-[180px] h-8 text-xs bg-white/5 border-white/10 text-white/70">
              <SelectValue placeholder="All Use Cases" />
            </SelectTrigger>
            <SelectContent className="bg-background border-white/10 max-h-[300px]">
              <SelectItem value="all" className="text-white/70">
                All Use Cases
              </SelectItem>
              {Object.entries(groupedUseCases).map(([group, useCases]) => (
                <SelectGroup key={group}>
                  <SelectLabel className="text-white/40 text-xs">
                    {group}
                  </SelectLabel>
                  {useCases.map((uc) => (
                    <SelectItem
                      key={uc.value}
                      value={uc.value}
                      className="text-white/70"
                    >
                      {uc.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-2 text-xs text-white/40 hover:text-white/70"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
